import { useAnimateHeight } from "@redotech/react-animation/transition";
import { useHandler } from "@redotech/react-util/hook";
import ChevronDown from "@redotech/redo-icons/chevron-down.svg";
import ChevronUp from "@redotech/redo-icons/chevron-up.svg";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import { Text } from "@redotech/redo-web/text";
import { memo, ReactNode, useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import * as styles from "./conversation-details-section.module.css";

export interface ConversationDetailsSectionProps {
  localStorageKey: string;
  title: string;
  defaultCollapsed?: boolean;
  children: ReactNode;
}

export const ConversationDetailsSection = memo(
  function ConversationDetailsSection({
    localStorageKey,
    title,
    defaultCollapsed = false,
    children,
  }: ConversationDetailsSectionProps) {
    const [collapsed, setCollapsed] = useLocalStorage<boolean>(
      localStorageKey,
      defaultCollapsed,
    );

    const [contentElement, setContentElement] = useState<HTMLElement | null>(
      null,
    );
    const [animateStyles, animating] = useAnimateHeight(contentElement);

    const handleToggle = useHandler(() => {
      setCollapsed((prev) => !prev);
    });

    return (
      <Flex
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        dir="column"
        gap="none"
        pb={collapsed && !animating ? "none" : "lg"}
      >
        <Flex
          align="center"
          className={styles.header}
          cursor="pointer"
          justify="space-between"
          onClick={handleToggle}
          py="lg"
        >
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {title}
          </Text>
          <Icon
            arbiterIconSvg={collapsed ? ChevronUp : ChevronDown}
            color="ghost"
            size="extra-small"
          />
        </Flex>

        <Flex
          dir="column"
          gap="md"
          overflow="hidden"
          ref={setContentElement}
          style={animateStyles}
        >
          {(animating || !collapsed) && children}
        </Flex>
      </Flex>
    );
  },
);
